import React, { useMemo, forwardRef, useImperativeHandle, useEffect } from 'react'
import { Form, Row, Col, Collapse, Input, Descriptions, Table, Button, Space } from '@bedrock/components'
import cx from "classnames";
import "./index.less";
import SFormItem, {getLabelTip} from "#components/SForm/SFormItem";
import { DescriptionsItemType } from "@bedrock/components/lib/Descriptions/Item";
import { localPx2rem } from '#utils';
import MPCFormGrid from '#components/MPCFormGrid';

export type FormItemDescriptionsItemType = React.ComponentProps<typeof SFormItem>
    & Omit<DescriptionsItemType, 'label' | 'children'>
    & {
        offsetAfter?: number; // 后面布局的偏移
    };

type FormItemDescriptionsProps = {
    items: FormItemDescriptionsItemType[]
    title?: string
    extra?: React.ReactNode
    colon?: boolean;
    column?: number;
    lightBorder?: boolean;
    renderData?: {
        detailData: any;
    } & any;
} & Omit<React.ComponentProps<typeof Descriptions>, 'items' | 'title' | 'column' | 'extra' | 'bordered' | 'colon'>;

// 空的 Descriptions.Item 参数类型
const emptyItem = {
    label: '',
    labelStyle: {
        backgroundColor: 'transparent',
        borderRight: 'none'
    },
    children: null
}


// 对齐 Descriptions.Item 的参数类型
const DescriptionsFormItem = function (props: FormItemDescriptionsProps) {
    const { items,
        className,
        contentStyle,
        labelStyle,
        column = 2,
        lightBorder = false,
        renderData: parentRenderData,
        ...restProps } = props;

    const showFormItems = useMemo(() => {
        let contentSpan = 0;
        const showItems = [];
        items.forEach((item, index) => {
            // 整理item
            const { contentStyle,
                labelStyle,
                span,
                label,
                required,
                view,
                show, // 是否显示
                renderData,
                labelWrapper,
                labelTip,
                children,
                offsetAfter,
                ...restItem } = item;
            // 不显示
            if (show === false) return;
            const showSpan = span || 1;

            // 换行span
            const wrapperSpan = (showSpan + contentSpan) % column;
            if (wrapperSpan && wrapperSpan < showSpan) {
                let emptySpan = column - contentSpan % column;
                showItems.push({
                    ...emptyItem,
                    span: emptySpan
                });
                contentSpan += emptySpan;
            }
            let showLabel: any = typeof label === 'function' ? label(renderData || parentRenderData) : label;
            if (labelTip) {
                showLabel = getLabelTip(label, labelTip)
            }

            const showRequired = Array.isArray(restItem?.rules) 
                ? restItem.rules.some((rule: any) => rule.required) || required
                : required;

            const editContentStyle = !view ? {
                padding: '9px 12px',
            } : {}
            showItems.push({
                label: <span className={cx({
                    'required-text': showRequired && !view
                })}>{showLabel}</span>,
                labelStyle,
                contentStyle: {
                    ...editContentStyle,
                    ...contentStyle,
                },
                span: showSpan,
                children: children || <SFormItem
                    renderData={renderData || parentRenderData}
                    required={required}
                    label={label}
                    key={index}
                    showLabel={false}
                    view={view}
                    {...restItem}
                    col={undefined}
                    layout="horizontal"
                />
            })
            contentSpan += showSpan;

            // 后置为空；
            if (offsetAfter) {
                // 如果后偏移导致换行，则不添加，或者补齐空白
                const afterEmpty = column - contentSpan % column;
                if (afterEmpty === column) {
                    // 正好等于colum，表示末尾没空，不添加
                } else if (afterEmpty < offsetAfter) {
                    // 不够偏移，补齐空白
                    showItems.push({
                        ...emptyItem,
                        span: afterEmpty
                    });
                    contentSpan += afterEmpty;
                } else {
                    // 足够偏移，添加偏移
                    showItems.push({
                        ...emptyItem,
                        span: offsetAfter
                    });
                    contentSpan += offsetAfter;
                }
            }
        });
        // 解决表格布局变动问题,固定首行
        showItems.unshift(...(new Array(column).fill({
            span: 1,
            labelStyle: {
                height: 0,
                padding: 0,
                borderBottom: 'none'
            },
            contentStyle: {
                height: 0,
                padding: 0,
                borderBottom: 'none'
            }
        })))
        return showItems;
    }, [items, column])  
    
    return (
        <MPCFormGrid
            size="small"
            className={cx('descriptions-form', className, { 'light-border': lightBorder })}
            contentStyle={{
                width: 'auto',
                verticalAlign: 'top',
                textAlign: 'left',
                ...contentStyle
            }}
            labelStyle={{
                width: localPx2rem(146),
                verticalAlign: 'top',
                textAlign: 'right',
                ...labelStyle
            }}
            title=""
            bordered
            column={column}
            {...restProps}
            items={showFormItems}
        />
    )
}

export default DescriptionsFormItem;
